import { Container, Graphics, Sprite } from 'pixi.js';
import { FESTIVAL } from '../../data/full/festival';
import type { EdgeSide, SetDefinition } from '../../data/types';
import { locale, t } from '../../shared/i18n';
import { setTempo, stageMinutes } from '../../sim/lineup';
import type { FestivalExit, SimState } from '../../sim/state';
import type { Family, RenderContext } from '../context';
import { createFollowspots, teamCenter } from '../followspot';
import type { Frame } from '../frame';
import { parseHexColor } from '../palette';
import { LABEL_FONT_PX, NAME_TEXTURE_SCALE, createLineTextures } from '../textures-names';
import { lookup, setTint } from '../util';
import { drawExitLook } from './exit-looks';
import { exitSpotsOf } from './exit-spots';
import { AHEAD_TOKENS } from './path-way';

const OUTLINE = parseHexColor('#2b2010');
const PASTILLE = { radius: 8, gap: 22 };
// The sunrise's pale ground swallows a beam as faint as the boss's.
const SPOT_BRIGHTNESS = { night: 0.85, day: 1 };
const PANEL = { pad: 9, gap: 34, corner: 10, lineHeight: LABEL_FONT_PX * 1.4 };

const TURN: Readonly<Record<EdgeSide, number>> = {
  right: 0,
  bottom: Math.PI / 2,
  left: Math.PI,
  top: -Math.PI / 2,
};

const INWARD: Readonly<Record<EdgeSide, { x: number; y: number }>> = {
  right: { x: -1, y: 0 },
  left: { x: 1, y: 0 },
  top: { x: 0, y: 1 },
  bottom: { x: 0, y: -1 },
};

const ARROWS: Readonly<Record<EdgeSide, (name: string) => string>> = {
  right: (name) => `${name} →`,
  left: (name) => `← ${name}`,
  top: (name) => `↑ ${name}`,
  bottom: (name) => `↓ ${name}`,
};

function bpmOf(set: SetDefinition): string {
  return setTempo(set).bpm.toLocaleString(locale(), {
    maximumFractionDigits: 1,
    numberingSystem: 'latn',
  });
}

function exitLines(exit: Pick<FestivalExit, 'side'>, set: SetDefinition): [string, string] {
  return [
    ARROWS[exit.side](t(set.name)),
    t('≈ {minutes} min · {bpm} BPM', { minutes: stageMinutes(set), bpm: bpmOf(set) }),
  ];
}

interface ExitView {
  readonly root: Container;
  readonly portal: Graphics;
  readonly overhead: Graphics;
  readonly pastilles: Graphics;
  readonly panel: Container;
  readonly backing: Graphics;
  readonly lines: [Sprite, Sprite];
  key: string;
}

export function createExits(ctx: RenderContext): Family {
  const floorLayer = ctx.layers.decor.addChild(new Container());
  const overheadLayer = ctx.layers.canopy.addChild(new Container());
  const panelLayer = ctx.layers.bubbles.addChild(new Container());
  const texts = createLineTextures();
  const spots = createFollowspots(ctx.layers.glow, ctx.layers.core, SPOT_BRIGHTNESS);
  const views: ExitView[] = [];

  function viewAt(index: number): ExitView {
    let view = views[index];
    if (view === undefined) {
      const root = floorLayer.addChild(new Container());
      const portal = root.addChild(new Graphics());
      const pastilles = root.addChild(new Graphics());
      const overhead = overheadLayer.addChild(new Graphics());
      const panel = panelLayer.addChild(new Container());
      const backing = panel.addChild(new Graphics());
      const lines: [Sprite, Sprite] = [panel.addChild(new Sprite()), panel.addChild(new Sprite())];
      for (const line of lines) {
        line.anchor.set(0.5, 0);
        line.scale.set(1 / NAME_TEXTURE_SCALE);
      }
      view = { root, portal, overhead, pastilles, panel, backing, lines, key: '' };
      views[index] = view;
    }
    return view;
  }

  function writePanel(view: ExitView, exit: FestivalExit, set: SetDefinition): void {
    const [name, info] = exitLines(exit, set);
    const [first, second] = view.lines;
    first.texture = texts.get(name, 700);
    second.texture = texts.get(info, 500);
  }

  return {
    update(state: SimState, _alpha: number, frame: Frame): void {
      const festival = state.festival;
      const exits = festival?.phase === 'sunrise' ? festival.exits : [];
      views.forEach((view, index) => {
        view.root.visible = view.overhead.visible = view.panel.visible = index < exits.length;
      });
      const { palette, light, camera, tempo } = frame;
      const full = FESTIVAL.departBars * tempo.ticksPerBar;
      const beats =
        (frame.now - (festival?.scenes.at(-1)?.toTick ?? frame.now)) / tempo.ticksPerBeat;
      const team = teamCenter(state.players, state.core, frame.now - state.tick);
      spots.begin();
      exitSpotsOf(beats, state.core, team, exits, frame.calm).forEach((spot, index) => {
        const exit = exits[index];
        if (spot !== null && exit !== undefined) {
          spots.show(index, spot, state, frame, exit.radius);
        }
      });
      spots.end();
      exits.forEach((exit, index) => {
        const view = viewAt(index);
        const set = lookup(ctx.stages, exit.setId, 'set');
        const key = `${exit.setId} ${exit.side} ${locale()}`;
        if (view.key !== key) {
          view.key = key;
          writePanel(view, exit, set);
        }
        view.root.position.set(exit.x, exit.y);
        // A player whose peer left has no say, as when the sim counts the departure.
        const present = (festival?.standing ?? []).filter(
          (_, slot) => state.players[slot]?.gone !== true,
        );
        const share =
          festival !== undefined && present.length > 0 && present.every((at) => at === index)
            ? Math.min(1, festival.departTicks / full)
            : 0;
        view.portal.rotation = view.overhead.rotation = TURN[exit.side];
        view.overhead.position.set(exit.x, exit.y);
        drawExitLook(view.portal.clear(), view.overhead.clear(), set.decor, exit.radius, {
          beats,
          kick: frame.pulse,
          calm: frame.calm,
          depart: share,
          turn: TURN[exit.side],
          ahead: palette[AHEAD_TOKENS[set.decor] ?? 'or'],
          gold: palette.or,
        });

        const g = view.pastilles.clear();
        const count = state.players.length;
        state.players.forEach((player, slot) => {
          const x = (slot - (count - 1) / 2) * PASTILLE.gap;
          const color = palette[lookup(ctx.classTokens, player.classId, 'class')];
          if (festival?.standing[slot] === index) {
            g.circle(x, 0, PASTILLE.radius + 2).fill(OUTLINE);
            g.circle(x, 0, PASTILLE.radius).fill(color);
          } else {
            g.circle(x, 0, PASTILLE.radius).stroke({ color: palette.texte, alpha: 0.55, width: 2 });
          }
        });
        view.pastilles.position.set(0, 0);

        layPanel(view, exit, camera.scale);
        setTint(view.backing, palette.sol);
        view.backing.alpha = light.additive ? 0.85 : 0.9;
        for (const line of view.lines) {
          setTint(line, palette.texte);
        }
        view.lines[1].alpha = 0.8;
      });
    },
    reset(): void {
      spots.reset();
      for (const view of views) {
        view.key = '';
      }
    },
    destroy(): void {
      spots.destroy();
      texts.destroy();
      floorLayer.destroy({ children: true });
      overheadLayer.destroy({ children: true });
      panelLayer.destroy({ children: true });
    },
  };
}

function layPanel(view: ExitView, exit: FestivalExit, scale: number): void {
  const [first, second] = view.lines;
  const width = Math.max(first.width, second.width) + 2 * PANEL.pad;
  const height = 2 * PANEL.lineHeight + 2 * PANEL.pad;
  view.backing
    .clear()
    .roundRect(-width / 2, 0, width, height, PANEL.corner)
    .fill(0xffffff);
  first.position.set(0, PANEL.pad);
  second.position.set(0, PANEL.pad + PANEL.lineHeight);
  const inward = INWARD[exit.side];
  const size = 1 / scale;
  const away = exit.radius + PANEL.gap * size;
  view.panel.scale.set(size);
  view.panel.position.set(
    exit.x + inward.x * (away + (width / 2) * size),
    exit.y + inward.y * (away + (height / 2) * size) - (height / 2) * size,
  );
}
